import React, { useState, useEffect } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';

import { useCreateScheduler, useGetSuiteSchedulers, useDeleteApiSchedular } from '../../../useQuery';
import { useNotification } from '../../../hooks';
import { useQueryClient } from '@tanstack/react-query';

const Days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SchedulerOptions = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const SchedulerTab = ({ suite }) => {
  const notify = useNotification();
  const queryClient = useQueryClient();
  const [frequency, setFrequency] = useState('once');
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [repeatInterval, setRepeatInterval] = useState('');

  const { data: schedulers, isLoading: isLoadingSchedulers } = useGetSuiteSchedulers(suite?.id, {
    onError: (error) => {
      notify(error.message || "Failed to fetch schedulers", "error");
    }
  });

  const { mutate: createScheduler, isPending: isCreating } = useCreateScheduler({
    onSuccess: () => {
      notify("Scheduler created successfully", "success");
      setSelectedTime(null);
      setSelectedDays([]);
      setSelectedDate(null);
      setRepeatInterval('');
      queryClient.invalidateQueries(["suiteSchedulers", suite?.id]);
    },
    onError: (error) => {
      notify(error.message || "Failed to create scheduler", "error");
    }
  });

  const { mutate: deleteScheduler, isPending: isDeleting } = useDeleteApiSchedular({
    onSuccess: () => {
      notify("Scheduler deleted successfully", "success");
      queryClient.invalidateQueries(["suiteSchedulers", suite?.id]);
    },
    onError: (error) => {
      notify(error.message || "Failed to delete scheduler", "error");
    }
  });

  const handleFrequencyChange = (event) => {
    setFrequency(event.target.value);
  };

  const handleIntervalChange = (event) => {
    const value = event.target.value;
    if (value === '' || (Number(value) >= 1 && Number(value) <= 60)) {
      setRepeatInterval(value);
    }
  };

  return (
    <div className="font-apple flex flex-row gap-8 w-full">
      <div>
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">Select Frequency</p>
          <FormControl fullWidth>
            <Select value={frequency} onChange={handleFrequencyChange}>
              {SchedulerOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <p className="text-sm text-gray-600 mb-2">Time</p>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                value={selectedTime}
                onChange={setSelectedTime}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-2">Repeat Every</p>
            <TextField
              type="number"
              value={repeatInterval}
              onChange={handleIntervalChange}
              placeholder="Enter minutes"
              sx={{ width: '170px' }}
              InputProps={{
                endAdornment: <InputAdornment position="end">mins</InputAdornment>,
                inputProps: { min: 1, max: 60 },
              }}
            />
          </div>

          {frequency === 'weekly' && (
            <div>
              <p className="text-sm text-gray-600 mb-2">Select Days</p>
              <FormControl sx={{ width: 257 }}>
                <Select
                  multiple
                  value={selectedDays}
                  onChange={(e) => setSelectedDays(e.target.value)}
                >
                  {Days.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}

          {frequency === 'monthly' && (
            <div>
              <p className="text-sm text-gray-600 mb-2">Select Date</p>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </div>
          )}
        </div>

        <div className="mt-6">
          <div className="flex gap-4">
            <button
              className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 transition-colors rounded cursor-pointer"
            >
              Delete
            </button>
            <button
              onClick={() => {
                if (!suite?.id) {
                  notify("Suite ID not found", "error");
                  return;
                }
                if (!selectedTime) {
                  notify("Please select a time", "error");
                  return;
                }

                const scheduleTime = dayjs(selectedTime).format("HH:mm:ss");
                const scheduleDate = selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");

                createScheduler({
                  frequency,
         
                  scheduleDate,
                  scheduleTime,
                  subfrequency: selectedDays.length > 0 ? selectedDays.join(",") : "none",
                  suiteId: suite.id,
                  timeInterval: repeatInterval || 0
                });
              }}
              disabled={isCreating}
              className={`px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded cursor-pointer ${
                isCreating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isCreating ? 'Creating...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 p-4 rounded-lg w-full">
        <h3 className="text-base font-medium mb-4">Scheduled List</h3>
        <div className="max-h-[300px] overflow-y-auto flex flex-col gap-3">
          {isLoadingSchedulers ? (
            <p className="text-sm text-gray-500">Loading schedulers...</p>
          ) : !schedulers?.length ? (
            <p className="text-sm text-gray-500">No schedulers found</p>
          ) : (
            schedulers.map((scheduler) => (
              <ScheduleItem
                key={scheduler.idScheduler}
                frequency={scheduler.frequency}
                time={dayjs(scheduler.scheduleTime, "HH:mm:ss").format("hh:mm A")}
                onDelete={() => deleteScheduler(scheduler.idScheduler)}
                isDeleting={isDeleting}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const ScheduleItem = ({ frequency, time, onDelete, isDeleting }) => {
  return (
    <div className="border border-gray-200 rounded p-3 bg-gray-50 flex justify-between items-center hover:bg-white transition-colors">
      <div>
        <p className="text-sm font-medium">Frequency: {frequency}</p>
        <p className="text-sm text-gray-500">Time: {time}</p>
      </div>
      <button
        onClick={onDelete}
        disabled={isDeleting}
        className={`text-red-600 hover:text-red-700 text-sm flex items-center gap-1 ${
          isDeleting ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <DeleteIcon fontSize="small" />
        {isDeleting ? 'Deleting...' : 'Delete'}
      </button>
    </div>
  );
};

export default SchedulerTab;
